"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { BrandingConfig, DEFAULT_BRANDING, applyBrandingToDocument } from "@/lib/branding";

interface BrandingContextType {
  branding: BrandingConfig;
  setBranding: (branding: BrandingConfig) => void;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
  organizationId?: string;
  initialBranding?: BrandingConfig;
}

export function BrandingProvider({
  children,
  organizationId,
  initialBranding,
}: BrandingProviderProps) {
  const [branding, setBrandingState] = useState<BrandingConfig>(
    initialBranding || DEFAULT_BRANDING
  );
  const [isLoading, setIsLoading] = useState(!initialBranding);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = async () => {
    if (!organizationId) {
      setBrandingState(DEFAULT_BRANDING);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/branding?organizationId=${organizationId}`);

      if (!response.ok) {
        throw new Error("Failed to fetch branding");
      }

      const data = await response.json();
      const fetchedBranding: BrandingConfig = {
        logo: data.logo || null,
        primaryColor: data.primaryColor || DEFAULT_BRANDING.primaryColor,
        secondaryColor: data.secondaryColor || DEFAULT_BRANDING.secondaryColor,
      };

      setBrandingState(fetchedBranding);
    } catch (err) {
      console.error("Failed to fetch branding:", err);
      setError(err instanceof Error ? err.message : "Failed to load branding");
      // Fall back to default branding on error
      setBrandingState(DEFAULT_BRANDING);
    } finally {
      setIsLoading(false);
    }
  };

  const setBranding = (newBranding: BrandingConfig) => {
    setBrandingState(newBranding);
    applyBrandingToDocument(newBranding);
  };

  // Fetch branding on mount if no initial branding provided
  useEffect(() => {
    if (!initialBranding && organizationId) {
      fetchBranding();
    }
  }, [organizationId]);

  // Apply branding whenever it changes
  useEffect(() => {
    applyBrandingToDocument(branding);
  }, [branding]);

  const value: BrandingContextType = {
    branding,
    setBranding,
    isLoading,
    error,
    refetch: fetchBranding,
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/**
 * Hook to use branding context
 */
export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}

/**
 * Hook to get current branding (safe version that doesn't require provider)
 */
export function useBrandingOptional() {
  const context = useContext(BrandingContext);
  return context || {
    branding: DEFAULT_BRANDING,
    setBranding: () => {},
    isLoading: false,
    error: null,
    refetch: async () => {},
  };
}
